import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { StagesEditor, AdvancedSettings } from "@/pages/wizard/StagesEditor";
import { editablePayload, weightsValid, subTotal } from "@/pages/wizard/jobUtils";
import { CustomQuestionsEditor } from "@/pages/job/CustomQuestionsEditor";
import { WorkSampleSettings } from "@/pages/job/ws/WorkSampleSettings";
import { CopyLinkButton, careersUrl } from "@/components/app/CopyLinkButton";
import { PrimaryJobFields, BarRequired } from "@/pages/job/LegalFields";
import { CollapsibleSection } from "@/components/app/CollapsibleSection";

export function JobSettingsTab({ job }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(job);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(job), [job]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = weightsValid(form.scoring_weights) && subTotal(form.resume_subweights) === 100;

  const save = async () => {
    setBusy(true);
    try {
      const { wizard_step, ...body } = editablePayload(form);
      body.custom_questions = (form.careers_page?.custom_questions || []).filter((q) => q.question.trim());
      await api.patch(`/jobs/${job.id}`, body);
      await qc.invalidateQueries({ queryKey: ["job", job.id] });
      toast.success("Job settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-tour="pt-job-settings-tab" data-testid="job-settings-tab">
      <section className="panel space-y-4 p-6">
        <PrimaryJobFields form={form} setForm={setForm} prefix="job-settings" />
        <CollapsibleSection title="More details (optional)" defaultOpen={false} testId="job-settings-more-details">
          <div className="grid gap-4 p-1 md:grid-cols-3">
            <div className="space-y-2"><Label>Department</Label><Input value={form.department || ""} onChange={set("department")} data-testid="job-settings-department-input" /></div>
            <div className="space-y-2"><Label>Employment type</Label><Input value={form.employment_type || ""} onChange={set("employment_type")} data-testid="job-settings-type-input" /></div>
            <BarRequired form={form} set={(k, v) => setForm((f) => ({ ...f, [k]: v }))} prefix="job-settings" />
          </div>
        </CollapsibleSection>
        <div className="flex flex-wrap items-center gap-3 text-sm text-ink-secondary">
          <span>Careers page: <Link to={`/careers/${job.careers_page?.slug}`} target="_blank" className="text-brand hover:underline" data-testid="job-settings-careers-link">/careers/{job.careers_page?.slug}</Link></span>
          <CopyLinkButton url={careersUrl(job)} size="sm" testId="job-settings-copy-careers-link" />
        </div>
        <div>
          <Button asChild variant="outline" size="sm" data-testid="job-settings-edit-requirements-btn">
            <Link to={`/app/jobs/new?draft=${job.id}&step=2`}><Pencil size={14} /> Edit requirements</Link>
          </Button>
        </div>
      </section>
      <StagesEditor job={form} setJob={setForm} />
      <WorkSampleSettings form={form} setForm={setForm} />
      <div className="panel flex flex-wrap items-center gap-3 p-5" data-tour="pt-experience-settings" data-testid="experience-settings">
        <Label htmlFor="gap-threshold" className="text-sm">Experience review: don't penalise career gaps shorter than</Label>
        <Input id="gap-threshold" type="number" min={0} max={120} className="h-9 w-20" value={form.experience_config?.gap_threshold_months ?? 12}
          onChange={(e) => setForm((f) => ({ ...f, experience_config: { gap_threshold_months: Number(e.target.value) } }))} data-testid="gap-threshold-input" />
        <span className="text-sm text-ink-secondary">months</span>
      </div>
      <CustomQuestionsEditor questions={form.careers_page?.custom_questions}
        onChange={(qs) => setForm((f) => ({ ...f, careers_page: { ...f.careers_page, custom_questions: qs } }))} />
      <AdvancedSettings job={form} setJob={setForm} defaultOpen />
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy || !valid} data-tour="pt-job-settings-save-btn" data-testid="job-settings-save-btn">
          {busy && <Loader2 size={16} className="animate-spin" />} Save changes
        </Button>
      </div>
    </div>
  );
}
